import React, { useState, useEffect, useRef } from 'react';
import Icon from './AppIcon';
import notificationService from '../services/notificationService';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const NotificationCenter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (user?.id) {
      loadNotifications();
      loadUnreadCount();
      
      // Subscribe to real-time notifications
      const channel = notificationService?.subscribeToNotifications(
        user?.id,
        handleNewNotification
      );

      return () => {
        notificationService?.unsubscribeFromNotifications();
      };
    }
  }, [user?.id]);

  const handleNewNotification = (notification) => {
    setNotifications(prev => [notification, ...prev]?.slice(0, 10));
    setUnreadCount(prev => prev + 1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const loadNotifications = async () => {
    setIsLoading(true);
    const { data } = await notificationService?.getUserNotifications(user?.id, {
      limit: 10
    });
    if (data) {
      setNotifications(data);
    }
    setIsLoading(false);
  };

  const loadUnreadCount = async () => {
    const { count } = await notificationService?.getUnreadCount(user?.id);
    setUnreadCount(count || 0);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification?.is_read) {
      await notificationService?.markAsRead(notification?.id);
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev =>
        prev?.map(n => (n?.id === notification?.id ? { ...n, is_read: true } : n))
      );
    }

    if (notification?.action_url) {
      navigate(notification?.action_url);
    }

    setIsOpen(false);
  };

  const handleMarkAllAsRead = async () => {
    await notificationService?.markAllAsRead(user?.id);
    setUnreadCount(0);
    setNotifications(prev => prev?.map(n => ({ ...n, is_read: true })));
  };

  const handleDeleteNotification = async (notificationId, e) => {
    e?.stopPropagation();
    await notificationService?.deleteNotification(notificationId);
    setNotifications(prev => prev?.filter(n => n?.id !== notificationId));
    loadUnreadCount();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return { name: 'CheckCircle2', color: 'var(--color-success)' };
      case 'error':
        return { name: 'AlertCircle', color: 'var(--color-destructive)' };
      case 'warning':
        return { name: 'AlertTriangle', color: 'var(--color-warning)' };
      case 'info':
      default:
        return { name: 'Info', color: 'var(--color-primary)' };
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date?.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-muted/50 transition-colors"
        aria-label="Notifications"
      >
        <Icon name="Bell" size={20} className="text-foreground" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 md:w-96 bg-card border border-border rounded-lg shadow-lg z-50 max-h-[500px] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-sm text-primary hover:text-primary/80 transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : notifications?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-3">
                  <Icon name="Bell" size={32} className="text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  No notifications yet
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications?.map((notification) => {
                  const iconConfig = getNotificationIcon(notification?.type);
                  return (
                    <div
                      key={notification?.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`p-4 hover:bg-muted/30 cursor-pointer transition-colors ${
                        !notification?.is_read ? 'bg-primary/5' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center">
                            <Icon
                              name={iconConfig?.name}
                              size={20}
                              color={iconConfig?.color}
                            />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="text-sm font-semibold text-foreground truncate">
                              {notification?.title}
                            </h4>
                            <button
                              onClick={(e) => handleDeleteNotification(notification?.id, e)}
                              className="flex-shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                              aria-label="Delete notification"
                            >
                              <Icon name="X" size={14} />
                            </button>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {notification?.message}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-muted-foreground">
                              {formatTimeAgo(notification?.created_at)}
                            </span>
                            {!notification?.is_read && (
                              <span className="w-2 h-2 rounded-full bg-primary"></span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications?.length > 0 && (
            <div className="p-3 border-t border-border">
              <button
                onClick={() => {
                  navigate('/notifications');
                  setIsOpen(false);
                }}
                className="w-full text-sm text-center text-primary hover:text-primary/80 transition-colors"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;